import React, { FC } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { ProjectResponse } from '@api/projects/mutations';
import { formatValue } from '@components/create-project/table/helpers';
import { CircularProgress } from '@mui/material';
import { useTranslation } from "react-i18next";

interface OfferGraphProps {
  offer: ProjectResponse | null;
}

export const LifeCycleCostGraph: FC<OfferGraphProps> = ({ offer }) => {
  const { t } = useTranslation();

  if (!offer) return <CircularProgress size={100}/>;

  const groundSourceData  = offer.outputs['Offer to customer']['Life-Cycle-Cost']['Ground-source'];
  const airSourceData  = offer.outputs['Offer to customer']['Life-Cycle-Cost']['Air-source'];
  const hybridData  = offer.outputs['Offer to customer']['Life-Cycle-Cost']['Hybrid'];
  const dhData  = offer.outputs['Offer to customer']['Life-Cycle-Cost']['DH'];

  const data = groundSourceData.map((value, index) => ({
    year: index + 1,
    "(1) Ground-source": value,
    "(2) Air-source": airSourceData[index],
    "(3) Hybrid": hybridData[index],
    "(4) DH": dhData[index],
  }));

  return (
    <ResponsiveContainer className="print:hidden" width="100%" style={{ paddingTop: '34px' }} height={500}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="year" />
        <YAxis
          domain={[0, 'dataMax + 100000']}
          tickFormatter={(value) => `${formatValue(value)}`}
        />
        <Tooltip
          formatter={(value: number) => [`€${formatValue(value)}`, ""]}
          labelFormatter={(value) => `Year ${value}`}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="(1) Ground-source"
          strokeWidth={2}
          stroke="#006064"
          dot={{ r: 3, strokeWidth: 1, fill: "#006064" }}
        />
        <Line
          type="monotone"
          dataKey="(2) Air-source"
          strokeWidth={2}
          stroke="#FF5722"
          dot={{ r: 3, strokeWidth: 1, fill: "#FF5722" }}
        />
        <Line
          type="monotone"
          dataKey="(3) Hybrid"
          strokeWidth={2}
          stroke="#2E7D32"
          dot={{ r: 3, strokeWidth: 1, fill: "#2E7D32" }}
        />
        <Line
          type="monotone"
          dataKey="(4) DH"
          strokeWidth={2}
          stroke="#0288D1"
          dot={{ r: 3, strokeWidth: 1, fill: "#0288D1" }}
        />
        <text
          x="50%"
          y={8}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize: '14px', fill: '#18181B', fontWeight: 500 }}
        >
          {t('project.table.priceInformation.lifeCycleGraph.title')}
        </text>
      </LineChart>
    </ResponsiveContainer>
  );
};
